// src/pages/user-profile-settings/index.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useLanguage } from '../../context/LanguageContext';
import { API_BASE_URL, apiRequest } from '../../services/apiClient';
import ProfileInformation from './components/ProfileInformation';
import ConnectedAccounts from './components/ConnectedAccounts';
import AccountSettings from './components/AccountSettings';
import SavedSearches from './components/SavedSearches';
import FavoriteProperties from './components/FavoriteProperties';
import BusinessProfile from './components/BusinessProfile';
import PrivacyControls from './components/PrivacyControls';
import PaymentMethods from './components/PaymentMethods';
import ActivityHistory from './components/ActivityHistory';
import MobileTabNavigation from './components/MobileTabNavigation';
import DesktopTabNavigation from './components/DesktopTabNavigation';

const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');
const normalizeRole = (role) => `${role || ''}`.toLowerCase();

const resolveAvatarUrl = (avatarUrl) => {
  if (!avatarUrl) {
    return '/assets/images/profile_default.png';
  }
  if (/^https?:\/\//i.test(avatarUrl)) {
    return avatarUrl;
  }
  if (avatarUrl.startsWith('/')) {
    return `${API_ORIGIN}${avatarUrl}`;
  }
  return `${API_ORIGIN}/${avatarUrl}`;
};

const UserProfileSettings = () => {
  const { user: authUser, updateUser } = useAuth();
  const { confirm } = useConfirm();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'unsaved' | 'saving' | 'saved' | 'error'
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [profile, setProfile] = useState(null);

  const tabs = [
    {
      id: 'profile',
      label: 'Profile',
      icon: 'User',
      component: ProfileInformation,
      roles: ['all']
    },
    {
      id: 'connections',
      label: t('Connection Details', 'Bağlantı Bilgileri'),
      icon: 'Link2',
      component: ConnectedAccounts,
      roles: ['all']
    },
    {
      id: 'account',
      label: 'Account Settings',
      icon: 'Settings',
      component: AccountSettings,
      roles: ['all']
    },
    {
      id: 'searches',
      label: 'Saved Searches',
      icon: 'Search',
      component: SavedSearches,
      roles: ['market_user', 'superadmin']
    },
    {
      id: 'favorites',
      label: 'Favorite Properties',
      icon: 'Heart',
      component: FavoriteProperties,
      roles: ['market_user', 'superadmin']
    },
    {
      id: 'business',
      label: 'Business Profile',
      icon: 'Building2',
      component: BusinessProfile,
      roles: ['market_user', 'superadmin']
    },
    {
      id: 'privacy',
      label: 'Privacy',
      icon: 'Shield',
      component: PrivacyControls,
      roles: ['all']
    },
    {
      id: 'payments',
      label: 'Payment Methods',
      icon: 'CreditCard',
      component: PaymentMethods,
      roles: ['market_user', 'superadmin']
    },
    {
      id: 'activity',
      label: 'Activity History',
      icon: 'Clock',
      component: ActivityHistory,
      roles: ['all']
    }
  ];

  const roleKey = normalizeRole(profile?.role || authUser?.role);
  const mappedUser = useMemo(
    () => ({
      id: profile?.id || authUser?.id || '',
      role: roleKey || 'market_user',
      name: profile?.fullName || authUser?.fullName || 'User',
      fullName: profile?.fullName || authUser?.fullName || 'User',
      email: profile?.email || authUser?.email || '',
      phone: profile?.phone || '',
      location: profile?.location || '',
      website: profile?.website || '',
      bio: profile?.bio || '',
      avatar: resolveAvatarUrl(profile?.avatarUrl || authUser?.avatarUrl),
      avatarUrl: profile?.avatarUrl || authUser?.avatarUrl || null,
      isVerified: true
    }),
    [profile, authUser, roleKey],
  );

  const filteredTabs = tabs.filter((tab) => tab.roles.includes('all') || tab.roles.includes(roleKey));

  useEffect(() => {
    let isCancelled = false;

    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        const profileData = await apiRequest('/profile/me', { auth: true });
        if (isCancelled) {
          return;
        }
        setProfile(profileData);
        updateUser(profileData);
        setAutoSaveStatus('saved');
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        if (!isCancelled) {
          setAutoSaveStatus('error');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      isCancelled = true;
    };
  }, [updateUser]);

  const handleTabChange = async (tabId) => {
    if (hasUnsavedChanges) {
      const shouldLeave = await confirm({
        title: 'Unsaved changes',
        message: 'You have unsaved changes. If you continue, unsaved edits will be lost.',
        confirmText: 'Leave',
        cancelText: 'Stay',
        tone: 'warning',
      });

      if (!shouldLeave) {
        return;
      }

      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');
    }

    setActiveTab(tabId);
    setIsMobileMenuOpen(false);
  };

  const handleDataChange = () => {
    setHasUnsavedChanges(true);
    setAutoSaveStatus('unsaved');
  };

  const handleProfileSave = async (payload) => {
    const trimmedFullName = payload?.fullName?.trim() || '';
    const trimmedEmail = payload?.email?.trim() || '';
    const trimmedPhone = payload?.phone?.trim() ?? '';
    const trimmedLocation = payload?.location?.trim() ?? '';
    const trimmedWebsite = payload?.website?.trim() ?? '';
    const trimmedBio = payload?.bio?.trim() ?? '';
    const fieldErrors = {};

    if (!trimmedFullName) {
      fieldErrors.fullName = t('Full name is required.', 'Ad soyad zorunludur.');
    }

    if (!trimmedEmail) {
      fieldErrors.email = t('Email address is required.', 'E-posta adresi zorunludur.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      fieldErrors.email = t('Please enter a valid email address.', 'Lütfen geçerli bir e-posta adresi girin.');
    }

    if (Object.keys(fieldErrors).length > 0) {
      setAutoSaveStatus('error');
      return {
        success: false,
        message: Object.values(fieldErrors)[0],
        fieldErrors,
      };
    }

    const body = {
      fullName: trimmedFullName,
      email: trimmedEmail,
      phone: trimmedPhone,
      location: trimmedLocation,
      website: trimmedWebsite,
      bio: trimmedBio,
    };

    setAutoSaveStatus('saving');
    try {
      const updatedProfile = await apiRequest('/profile/me', {
        method: 'PATCH',
        auth: true,
        body,
      });

      setProfile(updatedProfile);
      updateUser(updatedProfile);
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');
      return { success: true };
    } catch (error) {
      console.error('Failed to update profile:', error);
      setAutoSaveStatus('error');
      return {
        success: false,
        message: error?.message || t('Failed to save profile.', 'Profil kaydedilemedi.'),
      };
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!file) {
      return { success: false, message: 'Please select an image.' };
    }

    const formData = new FormData();
    formData.append('file', file);

    setIsAvatarUploading(true);
    setAutoSaveStatus('saving');
    try {
      const updatedProfile = await apiRequest('/profile/me/avatar', {
        method: 'POST',
        auth: true,
        body: formData,
      });

      setProfile(updatedProfile);
      updateUser(updatedProfile);
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');
      return { success: true };
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      setAutoSaveStatus('error');
      return {
        success: false,
        message: error?.message || 'Failed to upload avatar.',
      };
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    setIsAvatarUploading(true);
    setAutoSaveStatus('saving');
    try {
      const updatedProfile = await apiRequest('/profile/me/avatar', {
        method: 'DELETE',
        auth: true,
      });

      setProfile(updatedProfile);
      updateUser(updatedProfile);
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');
      return { success: true };
    } catch (error) {
      console.error('Failed to remove avatar:', error);
      setAutoSaveStatus('error');
      return {
        success: false,
        message: error?.message || 'Failed to remove avatar.',
      };
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleExportData = () => {
    const exportData = {
      profile: profile || mappedUser,
      exportDate: new Date().toISOString(),
      dataType: 'personal_data'
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `profile-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const ActiveTabComponent = filteredTabs.find((tab) => tab.id === activeTab)?.component;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
              <div className="h-8 bg-secondary-100 rounded w-1/3 mb-4 skeleton"></div>
              <div className="h-4 bg-secondary-100 rounded w-1/2 skeleton"></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <div className="space-y-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-12 bg-secondary-100 rounded skeleton"></div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-3">
                <div className="space-y-6">
                  <div className="h-64 bg-secondary-100 rounded-lg skeleton"></div>
                  <div className="h-48 bg-secondary-100 rounded-lg skeleton"></div>
                  <div className="h-32 bg-secondary-100 rounded-lg skeleton"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary font-heading mb-2">
                  Profile & Settings
                </h1>
                <p className="text-text-secondary">
                  Manage your account information and preferences
                </p>
              </div>

              <div className="mt-4 md:mt-0 flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm">
                  {autoSaveStatus === 'unsaved' && (
                    <>
                      <div className="w-2 h-2 bg-warning rounded-full"></div>
                      <span className="text-warning">Unsaved changes</span>
                    </>
                  )}
                  {autoSaveStatus === 'saving' && (
                    <>
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-text-secondary">Saving...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <div className="w-4 h-4 bg-success rounded-full flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-success">Saved</span>
                    </>
                  )}
                  {autoSaveStatus === 'error' && (
                    <>
                      <div className="w-4 h-4 bg-error rounded-full"></div>
                      <span className="text-error">Save failed</span>
                    </>
                  )}
                </div>

                <button
                  onClick={handleExportData}
                  className="text-sm font-medium text-primary hover:text-primary-700 transition-colors duration-200"
                >
                  Export Data
                </button>
              </div>
            </div>
          </div>

          <div className="lg:hidden mb-6">
            <MobileTabNavigation
              tabs={filteredTabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              isMenuOpen={isMobileMenuOpen}
              onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="hidden lg:block lg:col-span-1">
              <div className="lg:sticky lg:top-20 lg:max-h-screen lg:overflow-y-auto lg:pb-20">
                <DesktopTabNavigation
                  tabs={filteredTabs}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="min-h-screen lg:min-h-0">
                {ActiveTabComponent && (
                  <ActiveTabComponent
                    user={mappedUser}
                    onDataChange={handleDataChange}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onProfileSave={handleProfileSave}
                    onAvatarUpload={handleAvatarUpload}
                    onAvatarRemove={handleAvatarRemove}
                    isSaving={autoSaveStatus === 'saving'}
                    isAvatarUploading={isAvatarUploading}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfileSettings;
